import React, { PureComponent } from 'react';
import { reduxForm, change } from 'redux-form';
import { connect } from 'react-redux';
import * as R from 'ramda';

// Helpers
import classNames from 'classnames';
import { getActiveFilterButtontext, getKey } from 'Common/formValues';
import { gtmEventPush } from 'utils/gtm/events';

// Components
import BasicFiltersResults from 'Results/BasicFiltersResults';
import AdvancedFiltersResults from 'Results/AdvancedFiltersResults';
import Icon from 'Common/Icon';
import Checkbox from 'Common/Checkbox';
import SortBy from 'Results/SortBy';

// Styles
import bootstrap from 'vendor/bootstrap/_bootstrap_custom.scss';
import styles from 'components/FilterForm.scss';
import { RESET_POSTCODE } from '../../actions/actionTypes';

const fieldsHaveChanged = (a, b) => !R.equals(a, b);

const createActiveFields = R.compose(
  R.keys,
  R.filter(R.identity),
  R.omit(['lat', 'long', 'nearlynew', 'used', 'isPostCodeValid', 'sortBy']),
  R.ifElse(R.prop('isPostCodeValid'), R.identity, R.dissoc('withinDistance'))
);

/**
 * <FilterForm /> component.
 * @return {class} new FilterForm instance
 */
export class FilterForm extends PureComponent<*, *> {
  /**
   * @param {object} props - props passed
   */
  constructor(props: Object) {
    super(props);
    this.resetField = this.resetField.bind(this);
    this.resetAllFields = this.resetAllFields.bind(this);
  }

  /**
   * componentDidMount
   * @return {void}
   */
  componentDidMount() {}

  /* eslint-disable camelcase */
  /**
   * UNSAFE_componentWillReceiveProps
   * @param {object} nextProps - nextProps
   * @return {void}
   */
  UNSAFE_componentWillReceiveProps(nextProps) {
    if (
      this.props.initialized &&
      fieldsHaveChanged(this.props.fields, nextProps.fields)
    ) {
      const currentPropsWithoutPostCode = R.omit(
        ['postCode', 'lat', 'long'],
        this.props.fields
      );
      const nextPropsWithoutPostCode = R.omit(
        ['postCode', 'lat', 'long'],
        nextProps.fields
      );
      if (
        fieldsHaveChanged(currentPropsWithoutPostCode, nextPropsWithoutPostCode)
      ) {
        this.props.getResults();
      }
    }
  }

  /**
   * resetField.
   * @param {string} field - field
   *
   * @return {void}
   */
  resetField(field) {
    if (field === 'nearlynew' || field === 'used') {
      this.props.dispatch(change('filter', field, true));
    } else {
      this.props.dispatch(change('filter', field, null));
    }

    if (field === 'make') {
      this.props.dispatch(change('filter', 'model', null));
      this.props.dispatch(change('filter', 'trim', null));
    }

    if (field === 'model') {
      this.props.dispatch(change('filter', 'trim', null));
    }

    if (field === 'postCode') {
      this.props.dispatch({ type: RESET_POSTCODE });
      this.props.dispatch(change('filter', 'withinDistance', null));

      if (
        this.props.fields.sortBy &&
        getKey(this.props.fields.sortBy) === 'distance'
      ) {
        this.props.dispatch(change('filter', 'sortBy', null));
      }
    }
  }

  resetAllFields() {
    gtmEventPush({
      method: 'filterForm',
      args: {
        itemLabel: 'Clear all'
      }
    });
    R.keys(this.props.fields).forEach(field => {
      this.resetField(field);
    });
  }

  removeActiveFilter = key => {
    gtmEventPush({
      method: 'filterForm',
      args: {
        itemLabel: 'Remove Active Filter'
      }
    });
    this.resetField(key);
  };

  /**
   * Renders FilterForm.
   *
   * @return {JSX} rendered FilterForm.
   */
  render(): Element<*> | null {
    const {
      handleSubmit,
      fields,
      makes,
      models,
      trims,
      priceFrom,
      priceTo,
      isPostcodeValid,
      checkPostcode,
      additonalOptions,
      dealerName,
      fetchUpdatedQuery,
      resultsLength,
      ui
    } = this.props;

    // Persist postcode
    if (ui && ui.lastPostCode) {
      fields['postCode'] = ui.lastPostCode;
    }

    const activeFilters = createActiveFields(fields);

    return (
      <div>
        <SortBy
          fetchUpdatedQuery={fetchUpdatedQuery}
          selectedValue={fields.sortBy}
          isPostcode={fields.postCode}
        />
        {activeFilters.length >= 1 && (
          <div className={styles.activeFilters}>
            <div>
              Active Filters <span>({activeFilters.length})</span>
            </div>

            <button
              className={styles.activeFilterClearButton}
              onClick={this.resetAllFields}
            >
              Clear all
            </button>

            <div>
              {activeFilters.map(key => (
                <button
                  className={styles.activeFilterButton}
                  onClick={() => this.removeActiveFilter(key)}
                  key={key}
                >
                  {typeof fields[key] === 'boolean' && fields[key] && key}
                  {getActiveFilterButtontext(fields, key, dealerName)}
                  <Icon
                    iconName={'close'}
                    size={'small'}
                    className={styles.activeFilterIcon}
                  />
                </button>
              ))}
            </div>
          </div>
        )}
        <form
          onSubmit={handleSubmit}
          className={classNames(
            styles.filterForm,
            bootstrap.formInline,
            bootstrap.clearfix
          )}
        >
          <div className={styles.formGroup}>
            <div
              className={classNames(
                styles.doubleSelect,
                styles.doubleSelectNoPadding
              )}
            >
              <Checkbox
                name={'nearlynew'}
                label={'Nearly new'}
                selectedValue={fields.nearlynew}
              />
              <Checkbox
                name={'used'}
                label={'Used'}
                selectedValue={fields.used}
              />
            </div>
            <BasicFiltersResults
              styles={styles}
              makes={makes}
              models={models}
              trims={trims}
              fields={fields}
              priceFrom={priceFrom}
              priceTo={priceTo}
              resetField={this.resetField}
              checkPostcode={checkPostcode}
              isPostcodeValid={isPostcodeValid}
            />
            <AdvancedFiltersResults
              styles={styles}
              fields={fields}
              additonalOptions={additonalOptions}
              resetField={this.resetField}
              resultsLength={resultsLength}
            />
          </div>
        </form>
      </div>
    );
  }
}

function mapStateToProps(state, props) {
  return {
    initialValues: props.initialValues,
    ui: state.ui
  };
}

// eslint-disable-next-line no-class-assign
const ReduxFilterForm = reduxForm({
  form: 'filter',
  enableReinitialize: true,
  destroyOnUnmount: true
})(FilterForm);

export default connect(mapStateToProps)(ReduxFilterForm);



// WEBPACK FOOTER //
// ./src/Results/FilterForm/index.js